import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Bot, ChevronDown, HelpCircle, Gift, Trophy, Users, CreditCard, Shield, Sparkles, Loader2 } from 'lucide-react';
import { sendChatMessage } from '../lib/api';

// ============ FAQ QUICK ACTIONS (for suggestions) ============
const QUICK_QUESTIONS = [
  { icon: <HelpCircle size={14} />, question: '¿Qué es Cofres Mágicos?' },
  { icon: <Gift size={14} />, question: '¿Cómo participo?' },
  { icon: <Trophy size={14} />, question: '¿Cuáles son los premios?' },
  { icon: <Sparkles size={14} />, question: '¿Qué es modo Diamante?' },
  { icon: <CreditCard size={14} />, question: '¿Cómo pago mi cofre?' },
  { icon: <Users size={14} />, question: '¿Qué es Mi Red?' },
  { icon: <Shield size={14} />, question: '¿Es seguro participar?' },
  { icon: <Gift size={14} />, question: '¿Hay sorteos gratis?' },
];

const USER_QUICK_QUESTIONS = [
  { icon: <HelpCircle size={14} />, question: '¿Cómo veo mis cofres comprados?' },
  { icon: <CreditCard size={14} />, question: '¿Cuánto tiempo tengo para pagar?' },
  { icon: <Users size={14} />, question: '¿Cómo comparto mi link de referido?' },
  { icon: <Gift size={14} />, question: '¿Puedo abrir más de un cofre?' },
  { icon: <Trophy size={14} />, question: '¿Cómo sé si gané?' },
];

const ChatBotWidget = ({ isLoggedIn = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [showQuickActions, setShowQuickActions] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const quickFaqs = isLoggedIn
    ? [...QUICK_QUESTIONS.slice(0, 3), ...USER_QUICK_QUESTIONS.slice(0, 2)]
    : QUICK_QUESTIONS.slice(0, 5);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    // Initialize session ID
    if (!sessionId) {
      const stored = localStorage.getItem('chatbot_session_id');
      if (stored) {
        setSessionId(stored);
      } else {
        const newSession = `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem('chatbot_session_id', newSession);
        setSessionId(newSession);
      }
    }
  }, [sessionId]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        id: 'welcome',
        type: 'bot',
        text: isLoggedIn
          ? '¡Hola! 👋 Soy el asistente inteligente de Cofres Mágicos, potenciado por IA.\n\n¿En qué puedo ayudarte? Puedo responder cualquier pregunta sobre sorteos, premios, pagos, referidos y más.'
          : '¡Hola! 👋 Bienvenido a Cofres Mágicos. Soy tu asistente virtual con inteligencia artificial.\n\nPregúntame lo que quieras sobre nuestro sistema de rifas. ¡Estoy aquí para ayudarte!',
        time: new Date(),
      }]);
    }
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen, isLoggedIn, messages.length]);

  const addBotMessage = (text) => {
    setMessages(prev => [...prev, {
      id: `bot-${Date.now()}`,
      type: 'bot',
      text,
      time: new Date(),
    }]);
  };

  const handleSendMessage = async (text) => {
    if (!text.trim() || isLoading) return;

    setShowQuickActions(false);
    setMessages(prev => [...prev, {
      id: `user-${Date.now()}`,
      type: 'user',
      text: text.trim(),
      time: new Date(),
    }]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await sendChatMessage(text.trim(), sessionId);
      if (response.data.session_id && response.data.session_id !== sessionId) {
        setSessionId(response.data.session_id);
        localStorage.setItem('chatbot_session_id', response.data.session_id);
      }
      addBotMessage(response.data.response);
    } catch (error) {
      console.error('Chat error:', error);
      addBotMessage('Lo siento, hubo un problema al procesar tu mensaje. 😔\n\nPor favor intenta de nuevo o contáctanos por Telegram para atención personalizada.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickAction = (question) => {
    handleSendMessage(question);
  };

  const handleSend = () => {
    handleSendMessage(input);
  };

  const handleShowMore = () => {
    setShowQuickActions(false);
    const allQuestions = isLoggedIn
      ? [...QUICK_QUESTIONS, ...USER_QUICK_QUESTIONS]
      : QUICK_QUESTIONS;
    addBotMessage('📋 Preguntas sugeridas:\n\n' + allQuestions.map((q, i) => `${i + 1}. ${q.question}`).join('\n') + '\n\n¡O pregúntame cualquier otra cosa! Puedo ayudarte con dudas específicas sobre Cofres Mágicos.');
  };

  const handleNewChat = () => {
    const newSession = `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('chatbot_session_id', newSession);
    setSessionId(newSession);
    setMessages([{
      id: 'welcome-new',
      type: 'bot',
      text: '¡Nueva conversación iniciada! 🔄\n\n¿En qué puedo ayudarte?',
      time: new Date(),
    }]);
    setShowQuickActions(true);
  };

  return (
    <>
      {/* Floating button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-gradient-to-br from-[#FFC640] to-[#FFB020] text-[#090A0D] shadow-lg shadow-[#FFC640]/20 flex items-center justify-center hover:scale-110 transition-transform duration-200 group"
          aria-label="Abrir chat de ayuda"
        >
          <MessageCircle size={24} className="group-hover:scale-110 transition-transform" />
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-[8px] font-bold text-white animate-pulse">AI</span>
        </button>
      )}

      {/* Chat window */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-2rem)] h-[520px] max-h-[calc(100vh-4rem)] bg-[#0E1016] border border-[rgba(255,198,64,0.2)] rounded-2xl shadow-2xl shadow-black/40 flex flex-col overflow-hidden animate-in slide-in-from-bottom-5 duration-300">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#121521] to-[#161A2B] p-4 flex items-center justify-between border-b border-white/[0.06]">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#FFC640] to-[#FFB020] flex items-center justify-center relative">
                <Bot size={18} className="text-[#090A0D]" />
                <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-[6px] font-bold text-white">AI</span>
              </div>
              <div>
                <h3 className="text-sm font-heading font-bold text-[#F7F2E7] flex items-center gap-1.5">
                  Asistente CM
                  <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-gradient-to-r from-purple-500/20 to-pink-500/20 text-purple-300 border border-purple-500/30">GPT-4o</span>
                </h3>
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-[#35D07F] animate-pulse" />
                  <span className="text-[10px] text-[#35D07F]">En línea</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={handleNewChat}
                className="p-1.5 rounded-lg hover:bg-white/[0.06] text-[#F7F2E7]/40 hover:text-[#FFC640] transition-colors text-[10px]"
                title="Nueva conversación"
              >
                🔄
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 rounded-lg hover:bg-white/[0.06] text-[#F7F2E7]/40 hover:text-[#F7F2E7] transition-colors"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Messages area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 scrollbar-thin scrollbar-thumb-white/10">
            {messages.map((msg) => (
              <div key={msg.id} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${
                  msg.type === 'user'
                    ? 'bg-[#FFC640] text-[#090A0D] rounded-br-md'
                    : 'bg-[#1A1F2E] text-[#F7F2E7] border border-white/[0.06] rounded-bl-md'
                }`}>
                  <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.text}</p>
                  <p className={`text-[10px] mt-1 ${msg.type === 'user' ? 'text-[#090A0D]/40' : 'text-[#F7F2E7]/30'}`}>
                    {msg.time.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            ))}

            {/* Loading indicator */}
            {isLoading && (
              <div className="flex justify-start">
                <div className="max-w-[85%] rounded-2xl px-4 py-3 bg-[#1A1F2E] text-[#F7F2E7] border border-white/[0.06] rounded-bl-md">
                  <div className="flex items-center gap-2">
                    <Loader2 size={16} className="animate-spin text-[#FFC640]" />
                    <span className="text-sm text-[#F7F2E7]/60">Pensando...</span>
                  </div>
                </div>
              </div>
            )}

            {/* Quick action buttons */}
            {showQuickActions && messages.length > 0 && !isLoading && (
              <div className="space-y-2 pt-2">
                <p className="text-[10px] text-[#F7F2E7]/30 text-center">Preguntas sugeridas:</p>
                <div className="flex flex-wrap gap-1.5">
                  {quickFaqs.map((faq, i) => (
                    <button
                      key={i}
                      onClick={() => handleQuickAction(faq.question)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#1A1F2E] border border-white/[0.08] text-[#F7F2E7]/70 text-xs hover:border-[#FFC640]/30 hover:text-[#FFC640] transition-colors"
                    >
                      {faq.icon}
                      <span className="truncate max-w-[180px]">{faq.question}</span>
                    </button>
                  ))}
                  <button
                    onClick={handleShowMore}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-[rgba(255,198,64,0.06)] border border-[rgba(255,198,64,0.15)] text-[#FFC640] text-xs hover:bg-[rgba(255,198,64,0.1)] transition-colors"
                  >
                    <ChevronDown size={12} />
                    <span>Ver más</span>
                  </button>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input area */}
          <div className="p-3 border-t border-white/[0.06] bg-[#121521]">
            <div className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
                disabled={isLoading}
                className="flex-1 bg-[#0E1016] border border-white/[0.1] rounded-xl px-4 py-2.5 text-sm text-[#F7F2E7] placeholder:text-[#F7F2E7]/30 focus:outline-none focus:border-[#FFC640]/30 disabled:opacity-50"
                placeholder={isLoading ? "Esperando respuesta..." : "Escribe tu pregunta..."}
              />
              <button
                onClick={handleSend}
                disabled={!input.trim() || isLoading}
                className="w-10 h-10 rounded-xl bg-[#FFC640] text-[#090A0D] flex items-center justify-center hover:bg-[#FFD36B] disabled:opacity-30 disabled:hover:bg-[#FFC640] transition-colors"
              >
                {isLoading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              </button>
            </div>
            <p className="text-[9px] text-[#F7F2E7]/20 text-center mt-1.5">
              Asistente con IA · GPT-4o · Cofres Mágicos
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatBotWidget;
